import { CheckCircleIcon } from '@heroicons/react/solid';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import Turnstile, {
  isTurnstileEnabled,
  TURNSTILE_ERROR_MESSAGE,
  type TurnstileHandle,
} from '@/components/Turnstile';

import { WORKSHOP_CONTACT_TURNSTILE_ACTION } from '@/constants/turnstile';
import { axiosInstance } from '@/utils/httpClient';
import { WORKSHOP_CONTACT_ID } from '@/utils/scrollToSection';

const benefits = [
  "Boost your team's skills and productivity with a workshop tailored to your business needs and goals.",
  'Engage your team with interactive, hands-on learning that addresses real challenges, sparks collaboration, and drives immediate impact.',
  'Walk away with actionable frameworks, ready-to-use templates, and a clear implementation plan your team can apply from day one.',
];

const teamSizes = [
  { value: '25k-50k', label: '5-10 pax' },
  { value: '50k-100k', label: '11-20 pax' },
  { value: 'over_100k', label: '20+ pax' },
];

const inputClassName =
  'block w-full rounded-lg border-0 px-3.5 py-2.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:bg-slate-800 dark:text-slate-100 dark:ring-slate-700 dark:placeholder:text-slate-500 dark:focus:ring-indigo-500 sm:text-sm sm:leading-6';

const labelClassName =
  'block text-sm font-semibold leading-6 text-slate-900 dark:text-slate-200';

export default function ContactUsWorkshop() {
  const [isLoading, setIsLoading] = useState(false);
  const turnstileRef = useRef<TurnstileHandle>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsLoading(true);

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const turnstileToken = await turnstileRef.current?.getToken();
    if (isTurnstileEnabled && !turnstileToken) {
      toast.error(TURNSTILE_ERROR_MESSAGE);
      turnstileRef.current?.reset();
      setIsLoading(false);
      return;
    }

    const data = {
      firstName: formData.get('first-name'),
      lastName: formData.get('last-name'),
      email: formData.get('email'),
      company: formData.get('company'),
      phone: formData.get('phone'),
      message: formData.get('message'),
      teamSize: formData.get('budget'),
      turnstileToken,
    };

    try {
      const { data: response } = await axiosInstance.post(
        '/api/workshop-contact',
        data,
        { skipErrorToast: true },
      );

      if (response.message === 'Email sent successfully') {
        form.reset();
        toast.success("Thanks! We'll be in touch about your workshop shortly.");
      }
    } catch (error: any) {
      console.error('Error:', error);
      toast.error(
        (error?.response?.data?.error as string | undefined) ||
          'Failed to send message. Please try again.',
      );
    } finally {
      // Tokens are single-use — refresh for the next attempt either way.
      turnstileRef.current?.reset();
      setIsLoading(false);
    }
  };

  return (
    <section
      id={WORKSHOP_CONTACT_ID}
      className='scroll-mt-24 border-t border-slate-100 bg-slate-50 py-24 dark:border-slate-900 dark:bg-slate-950 sm:py-32'
    >
      <div className='mx-auto grid max-w-7xl grid-cols-1 gap-x-16 gap-y-12 px-6 lg:grid-cols-2 lg:px-8'>
        {/* Left: heading, benefits, video */}
        <div className='flex flex-col'>
          <h2 className='text-base font-semibold leading-7 text-indigo-600 dark:text-indigo-500'>
            For Teams &amp; Organizations
          </h2>
          <p className='mt-2 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-4xl'>
            Custom workshop for your team
          </p>
          <p className='mt-4 text-lg leading-8 text-slate-600 dark:text-slate-400'>
            Bring Easy-Peasy.AI to your whole team with a private, hands-on
            session built around your goals.
          </p>

          <ul role='list' className='mt-8 space-y-4'>
            {benefits.map((benefit) => (
              <li key={benefit} className='flex items-start gap-x-3'>
                <CheckCircleIcon
                  className='mt-1 h-5 w-5 flex-none text-indigo-600 dark:text-indigo-500'
                  aria-hidden='true'
                />
                <span className='text-base leading-7 text-slate-600 dark:text-slate-400'>
                  {benefit}
                </span>
              </li>
            ))}
          </ul>

          <div className='mt-10 aspect-video w-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-950 shadow-lg dark:border-slate-800'>
            <iframe
              src='https://www.youtube.com/embed/mnaa4thYeq4?si=qO9tNsuNrbgzJ2t9'
              title='YouTube video player'
              className='h-full w-full'
              frameBorder={0}
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
              referrerPolicy='strict-origin-when-cross-origin'
              allowFullScreen
            />
          </div>
        </div>

        {/* Right: form card */}
        <div className='lg:pl-8'>
          <form
            onSubmit={handleSubmit}
            method='POST'
            className='rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-8'
          >
            <div className='grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2'>
              <div>
                <label htmlFor='first-name' className={labelClassName}>
                  First name
                </label>
                <div className='mt-2'>
                  <input
                    type='text'
                    name='first-name'
                    id='first-name'
                    required
                    autoComplete='given-name'
                    className={inputClassName}
                  />
                </div>
              </div>
              <div>
                <label htmlFor='last-name' className={labelClassName}>
                  Last name
                </label>
                <div className='mt-2'>
                  <input
                    type='text'
                    name='last-name'
                    id='last-name'
                    required
                    autoComplete='family-name'
                    className={inputClassName}
                  />
                </div>
              </div>
              <div className='sm:col-span-2'>
                <label htmlFor='email' className={labelClassName}>
                  Email
                </label>
                <div className='mt-2'>
                  <input
                    id='email'
                    name='email'
                    type='email'
                    required
                    autoComplete='email'
                    className={inputClassName}
                  />
                </div>
              </div>
              <div className='sm:col-span-2'>
                <label htmlFor='company' className={labelClassName}>
                  Company
                </label>
                <div className='mt-2'>
                  <input
                    type='text'
                    name='company'
                    id='company'
                    autoComplete='organization'
                    className={inputClassName}
                  />
                </div>
              </div>
              <div className='sm:col-span-2'>
                <label htmlFor='phone' className={labelClassName}>
                  Phone
                </label>
                <div className='mt-2'>
                  <input
                    type='tel'
                    name='phone'
                    id='phone'
                    autoComplete='tel'
                    className={inputClassName}
                  />
                </div>
              </div>
              <div className='sm:col-span-2'>
                <div className='flex items-center justify-between'>
                  <label htmlFor='message' className={labelClassName}>
                    How can we help you?
                  </label>
                  <p className='text-xs text-slate-400 dark:text-slate-500'>
                    Max 500 characters
                  </p>
                </div>
                <div className='mt-2'>
                  <textarea
                    id='message'
                    name='message'
                    rows={4}
                    maxLength={500}
                    className={inputClassName}
                    defaultValue=''
                  />
                </div>
              </div>
              <fieldset className='sm:col-span-2'>
                <legend className={labelClassName}>Your team size</legend>
                <div className='mt-3 grid grid-cols-3 gap-3'>
                  {teamSizes.map((size, idx) => (
                    <label
                      key={size.value}
                      className='flex cursor-pointer items-center justify-center rounded-lg px-3 py-2.5 text-center text-sm font-medium text-slate-600 ring-1 ring-inset ring-slate-300 transition-colors hover:bg-slate-50 has-[:checked]:bg-indigo-50 has-[:checked]:font-semibold has-[:checked]:text-indigo-700 has-[:checked]:ring-2 has-[:checked]:ring-indigo-600 dark:text-slate-300 dark:ring-slate-700 dark:hover:bg-slate-800 dark:has-[:checked]:bg-indigo-500/10 dark:has-[:checked]:text-indigo-400 dark:has-[:checked]:ring-indigo-500'
                    >
                      <input
                        type='radio'
                        name='budget'
                        value={size.value}
                        defaultChecked={idx === 0}
                        className='sr-only'
                      />
                      {size.label}
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>

            <Turnstile
              ref={turnstileRef}
              action={WORKSHOP_CONTACT_TURNSTILE_ACTION}
              context='contact'
              className='mt-6'
            />

            <button
              type='submit'
              disabled={isLoading}
              className='mt-8 inline-flex w-full items-center justify-center gap-x-2 rounded-xl bg-indigo-600 px-3.5 py-3 text-sm font-bold text-white shadow-sm transition-all duration-150 hover:bg-indigo-500 hover:shadow focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-wait disabled:opacity-70'
            >
              {isLoading && (
                <svg
                  className='h-4 w-4 animate-spin'
                  viewBox='0 0 24 24'
                  fill='none'
                  aria-hidden='true'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  />
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                  />
                </svg>
              )}
              {isLoading ? 'Sending...' : 'Send message'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
